import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.light-platform.com/api';

const SuccessMessage = ({ message }) => (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-start">
        <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
        <p className="text-green-800">{message}</p>
    </div>
);

const ErrorMessage = ({ message }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start">
        <AlertCircle className="text-red-500 mr-3 flex-shrink-0" size={20} />
        <p className="text-red-800">{message}</p>
    </div>
);

const FieldError = ({ error }) => {
    if (!error) return null;
    return <p className="text-red-500 text-sm mt-1">{error}</p>;
};

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');
    const [isValidating, setIsValidating] = useState(true);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [notification, setNotification] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSuccess, setIsSuccess] = useState(false);

    const [formData, setFormData] = useState({
        password: '',
        password_confirmation: ''
    });

    // Получение параметров из URL
    useEffect(() => {
        const tokenParam = searchParams.get('token');
        const emailParam = searchParams.get('email');

        if (!tokenParam || !emailParam) {
            setIsValidating(false);
            setIsTokenValid(false);
            setNotification({
                type: 'error',
                message: 'Отсутствуют необходимые параметры в ссылке'
            });
            return;
        }

        setToken(tokenParam);
        setEmail(emailParam);
        validateToken(tokenParam, emailParam);
    }, [searchParams]);

    // Проверка токена на сервере
    const validateToken = async (tokenValue, emailValue) => {
        setIsValidating(true);

        try {
            const response = await fetch(`${API_URL}/validate-reset-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: tokenValue,
                    email: emailValue
                })
            });

            const data = await response.json();

            if (data.success) {
                setIsTokenValid(true);
                setNotification({
                    type: 'success',
                    message: 'Токен действителен. Вы можете установить новый пароль.'
                });
            } else {
                setIsTokenValid(false);
                setNotification({
                    type: 'error',
                    message: data.message || 'Токен недействителен или истек'
                });
            }
        } catch (error) {
            console.error('Ошибка проверки токена:', error);
            setIsTokenValid(false);
            setNotification({
                type: 'error',
                message: 'Произошла ошибка при проверке токена'
            });
        } finally {
            setIsValidating(false);
        }
    };

    // Валидация формы
    const validateForm = () => {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'Введите новый пароль';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Пароль должен содержать минимум 8 символов';
        }

        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Подтвердите пароль';
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Пароли не совпадают';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Отправка формы
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setNotification(null);

        try {
            const response = await fetch(`${API_URL}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                    email: email,
                    password: formData.password,
                    password_confirmation: formData.password_confirmation
                })
            });

            const data = await response.json();

            if (data.success) {
                setIsSuccess(true);
                setNotification({
                    type: 'success',
                    message: 'Пароль успешно изменён! Вы будете перенаправлены на страницу входа...'
                });

                // Редирект через 3 секунды
                setTimeout(() => {
                    navigate('/auth');
                }, 3000);
            } else {
                setNotification({
                    type: 'error',
                    message: data.message || 'Не удалось изменить пароль'
                });
            }
        } catch (error) {
            console.error('Ошибка сброса пароля:', error);
            setNotification({
                type: 'error',
                message: 'Произошла ошибка при изменении пароля'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Расчёт силы пароля
    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, text: '', color: '' };

        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.length >= 12) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z\d]/.test(password)) strength++;

        if (strength <= 2) return { strength: 1, text: 'Слабый', color: 'bg-red-500' };
        if (strength <= 4) return { strength: 2, text: 'Средний', color: 'bg-yellow-500' };
        return { strength: 3, text: 'Сильный', color: 'bg-green-500' };
    };

    const passwordStrength = getPasswordStrength(formData.password);

    // Состояние загрузки
    if (isValidating) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-800">Проверка токена...</h2>
                    <p className="text-gray-600 mt-2">Пожалуйста, подождите</p>
                </div>
            </div>
        );
    }

    // Недействительный токен
    if (!isTokenValid) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="text-red-500" size={32} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Токен недействителен</h2>
                    <p className="text-gray-600 mb-6">
                        Ссылка для восстановления пароля недействительна или истекла.
                        Пожалуйста, запросите новую ссылку.
                    </p>
                    <button
                        onClick={() => navigate('/auth')}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Вернуться на страницу входа
                    </button>
                </div>
            </div>
        );
    }

    // Успешное изменение пароля
    if (isSuccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="text-green-500" size={32} />
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-2">Пароль изменён!</h2>
                    <p className="text-gray-600 mb-6">
                        Ваш пароль был успешно изменён. Вы можете войти в систему с новым паролем.
                    </p>
                    <button
                        onClick={() => navigate('/auth')}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium shadow-lg"
                    >
                        Перейти на страницу входа
                    </button>
                </div>
            </div>
        );
    }

    // Форма сброса пароля
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                {/* Заголовок */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <KeyRound className="text-white" size={32} />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        Новый пароль
                    </h1>
                    <p className="text-gray-600">
                        Создайте надёжный пароль для вашего аккаунта
                    </p>
                </div>

                {/* Уведомления */}
                {notification && (
                    <div className="mb-6">
                        {notification.type === 'success' ? (
                            <SuccessMessage message={notification.message} />
                        ) : (
                            <ErrorMessage message={notification.message} />
                        )}
                    </div>
                )}

                {/* Информация об аккаунте */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-600">
                        <strong>Аккаунт:</strong> {email}
                    </p>
                </div>

                {/* Форма */}
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Новый пароль */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Lock size={16} className="inline mr-1" />
                            Новый пароль *
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={(e) => {
                                    setFormData({ ...formData, password: e.target.value });
                                    setErrors({ ...errors, password: null });
                                }}
                                className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                    errors.password ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Введите новый пароль"
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <FieldError error={errors.password} />

                        {/* Индикатор силы пароля */}
                        {formData.password && (
                            <div className="mt-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-gray-600">Надёжность пароля:</span>
                                    <span className={`text-xs font-medium ${
                                        passwordStrength.strength === 1 ? 'text-red-500' :
                                            passwordStrength.strength === 2 ? 'text-yellow-500' :
                                                'text-green-500'
                                    }`}>
                                        {passwordStrength.text}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                                        style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Подтверждение пароля */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Lock size={16} className="inline mr-1" />
                            Подтвердите пароль *
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={formData.password_confirmation}
                                onChange={(e) => {
                                    setFormData({ ...formData, password_confirmation: e.target.value });
                                    setErrors({ ...errors, password_confirmation: null });
                                }}
                                className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                    errors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Повторите новый пароль"
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <FieldError error={errors.password_confirmation} />
                    </div>

                    {/* Кнопка отправки */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Изменение пароля...' : 'Изменить пароль'}
                    </button>
                </form>

                {/* Ссылка на вход */}
                <div className="text-center mt-6">
                    <button
                        onClick={() => navigate('/auth')}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                        Вернуться на страницу входа
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
